import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Split, Plus, Trash2, AlertTriangle } from "lucide-react";

/**
 * Dimension Split (Iter-190) — inverse of merge.
 * Splits one over-generic cost centre into multiple targets based on rules.
 * Rule types: `account_id` · `party_id` · `narration_contains`. First match
 * wins per voucher line. Unmatched lines fall back to a default target (or
 * stay on the source).
 */
export default function SplitCcDialog({ open, onOpenChange, ccs, parties, accounts, initialSrc, onDone }) {
  const [src, setSrc] = useState("");
  const [rules, setRules] = useState([]);         // [{type, value, target_id}]
  const [defaultTgt, setDefaultTgt] = useState("");
  const [deleteSource, setDeleteSource] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (open) {
      setSrc(initialSrc || "");
      setRules([{ type: "narration_contains", value: "", target_id: "" }]);
      setDefaultTgt(""); setDeleteSource(false); setReport(null);
    }
  }, [open, initialSrc]);

  const ccOptions = useMemo(() => (ccs || []).map((c) => ({
    id: c.id, label: `${c.code || "—"} · ${c.name}`,
  })), [ccs]);
  const acctOptions = useMemo(() => (accounts || []).map((a) => ({
    id: a.id, label: `${a.code || "—"} · ${a.name}`,
  })), [accounts]);
  const partyOptions = useMemo(() => (parties || []).map((p) => ({
    id: p.id, label: `${p.name} (${p.party_type || "party"})`,
  })), [parties]);

  const addRule = () => setRules((r) => [...r, { type: "narration_contains", value: "", target_id: "" }]);
  const removeRule = (i) => setRules((r) => r.filter((_, idx) => idx !== i));
  const setRule = (i, patch) => setRules((r) => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const submit = async () => {
    if (!src) { toast.error("Pick the source dimension"); return; }
    const cleaned = rules
      .filter((r) => r.target_id && r.value)
      .map((r) => {
        const out = { target_id: r.target_id };
        if (r.type === "account_id") out.account_id = r.value;
        else if (r.type === "party_id") out.party_id = r.value;
        else out.narration_contains = r.value;
        return out;
      });
    if (cleaned.length === 0) {
      toast.error("Add at least one rule with a target and value"); return;
    }
    if (cleaned.some((r) => r.target_id === src) || defaultTgt === src) {
      toast.error("A target cannot be the source"); return;
    }
    setBusy(true);
    try {
      const payload = { rules: cleaned };
      if (defaultTgt) payload.default_target_id = defaultTgt;
      if (deleteSource) payload.delete_source = true;
      const r = await api.post(`/cost-centres/${src}/split`, payload);   // legacy split endpoint (kept)
      setReport(r.data);
      toast.success(
        `Split · ${r.data.lines_rewritten} lines re-tagged · ${r.data.unmatched} unmatched`
      );
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="cc-split-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="w-4 h-4 text-blue-600" /> Split dimension
          </DialogTitle>
          <DialogDescription>
            One over-generic dimension → many. Every voucher line currently tagged
            with the source is re-tagged to the first matching rule's target.
            Unmatched lines fall back to the default (or stay on the source).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Source (over-generic dimension)</Label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cc-split-src">
                <SelectValue placeholder="Pick the dimension to split" />
              </SelectTrigger>
              <SelectContent>
                {ccOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Rules (first match wins per line)</Label>
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                        onClick={addRule} data-testid="cc-split-add-rule">
                <Plus className="w-3.5 h-3.5" /> Add rule
              </Button>
            </div>
            <div className="space-y-2">
              {rules.length === 0 && (
                <div className="text-xs text-muted-foreground italic">No rules — click "Add rule" to start.</div>
              )}
              {rules.map((r, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_160px_28px] gap-2 items-center rounded-sm border p-2 bg-muted/20"
                      data-testid={`cc-split-rule-${i}`}>
                  <Select value={r.type} onValueChange={(v) => setRule(i, { type: v, value: "" })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="narration_contains">Narration ⊇</SelectItem>
                      <SelectItem value="account_id">Account</SelectItem>
                      <SelectItem value="party_id">Party</SelectItem>
                    </SelectContent>
                  </Select>
                  {r.type === "narration_contains" ? (
                    <Input value={r.value} onChange={(e) => setRule(i, { value: e.target.value })}
                            placeholder="text substring (case-insensitive)"
                            className="rounded-sm h-8 text-xs"
                            data-testid={`cc-split-rule-value-${i}`} />
                  ) : r.type === "account_id" ? (
                    <Select value={r.value} onValueChange={(v) => setRule(i, { value: v })}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`cc-split-rule-value-${i}`}>
                        <SelectValue placeholder="Pick account" />
                      </SelectTrigger>
                      <SelectContent>
                        {acctOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={r.value} onValueChange={(v) => setRule(i, { value: v })}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`cc-split-rule-value-${i}`}>
                        <SelectValue placeholder="Pick party" />
                      </SelectTrigger>
                      <SelectContent>
                        {partyOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={r.target_id} onValueChange={(v) => setRule(i, { target_id: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`cc-split-rule-target-${i}`}>
                      <SelectValue placeholder="→ target CC" />
                    </SelectTrigger>
                    <SelectContent>
                      {ccOptions.filter((o) => o.id !== src).map((o) =>
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => removeRule(i)}
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            data-testid={`cc-split-rule-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Fallback for unmatched lines (optional)</Label>
            <Select value={defaultTgt || "__none__"}
                      onValueChange={(v) => setDefaultTgt(v === "__none__" ? "" : v)}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cc-split-default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Leave on source (safe default)</SelectItem>
                {ccOptions.filter((o) => o.id !== src).map((o) =>
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={deleteSource} onCheckedChange={(v) => setDeleteSource(!!v)}
                        data-testid="cc-split-delete-source" />
            Delete the source dimension if every line was successfully re-tagged.
          </label>

          {report && (
            <div className="rounded-sm border border-emerald-300 bg-emerald-50 p-3 text-xs space-y-1" data-testid="cc-split-report">
              <div className="font-mono uppercase tracking-widest text-[10px] text-emerald-700">Split report</div>
              <div>Source: <b>{report.source}</b></div>
              <div>Lines re-tagged: <span className="font-mono">{report.lines_rewritten}</span></div>
              <div>Unmatched (fell back / stayed on source): <span className="font-mono">{report.unmatched}</span></div>
              <div>Source {report.source_deleted ? <b>deleted</b> : "kept"}.</div>
              <div className="pt-1 space-y-0.5">
                {(report.per_target || []).map((t) => (
                  <div key={t.target_id}>→ <b>{t.target_name}</b>: <span className="font-mono">{t.lines}</span> lines</div>
                ))}
              </div>
            </div>
          )}

          {rules.some((r) => r.target_id === src) && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> A rule's target cannot be the source.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={submit} disabled={busy || !src}
                  className="rounded-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white gap-1.5"
                  data-testid="cc-split-submit">
            <Split className="w-3.5 h-3.5" /> {busy ? "Splitting…" : "Apply split"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
